import React, { useEffect, useState } from "react";
import { useProperty } from "@/context/PropertyContext";
import { useTheme } from "@/context/ThemeContext";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, ComposedChart, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell, PieChart, Pie,
} from "recharts";
import { TrendingUp, Receipt, Calculator, Building2, Clock, Users, Info, ArrowUpDown } from "lucide-react";
import { eur } from "@/lib/format";

const MESES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

function MiniStat({ label, value, accent, sub }) {
  const colors = { success: "text-success", danger: "text-destructive", primary: "text-primary", warning: "text-warning" };
  return (
    <div className="data-card p-4">
      <p className="overline">{label}</p>
      <p className={`font-mono text-lg font-semibold mt-1.5 truncate ${colors[accent] || "text-foreground"}`} title={value}>
        {value}
      </p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5 truncate">{sub}</p>}
    </div>
  );
}

export default function Statistics() {
  const { selectedId, selected } = useProperty();
  const { theme } = useTheme();
  const year = new Date().getFullYear();
  const years = [year, year - 1, year - 2, year - 3, year - 4];
  const [anio, setAnio] = useState(String(year));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!selectedId) return;
    setLoading(true);
    api.get(`/stats?property_id=${selectedId}&anio=${anio}`)
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedId, anio]);

  const chartGrid = "hsl(var(--border))";
  const chartTooltip = {
    borderRadius: 8,
    border: "1px solid hsl(var(--border))",
    background: "hsl(var(--popover))",
    color: "hsl(var(--popover-foreground))",
    fontSize: 13,
  };
  const chartTick = { fontSize: 12, fill: "hsl(var(--muted-foreground))" };

  if (!selectedId)
    return <div className="data-card p-10 text-center text-muted-foreground">Selecciona o crea un piso primero.</div>;

  const serieAnual = (data?.serie_anual || []).map((s) => ({
    anio: s.anio,
    ingresos: Math.round(s.ingresos || 0),
    suministros: Math.round(s.gastos_suministros || 0),
  }));
  const curYear = serieAnual.find((s) => s.anio === anio);
  const totalIngresos = curYear?.ingresos || 0;
  const totalSuministros = curYear?.suministros || 0;
  const totalGastos = Math.round((data?.serie_anual || []).find((s) => s.anio === anio)?.gastos || 0);
  const totalBalance = totalIngresos - totalGastos;

  const mensual = (data?.mensual || []).map((m, i) => ({
    ...m,
    mes: MESES[i],
    ingresos: Math.round(m.ingresos || 0),
    gastos: Math.round(m.gastos || 0),
  }));
  const topInquilinos = data?.top_inquilinos || [];
  const gastosPorConcepto = data?.gastos_por_concepto || [];
  const ocupacion = data?.ocupacion_mensual || [];
  const puntualidad = data?.puntualidad || { a_tiempo: 0, vencidos: 0 };
  const totalPuntual = puntualidad.a_tiempo + puntualidad.vencidos;

  const GASTO_COLORS = [
    "hsl(4 100% 60%)", "hsl(37 91% 55%)", "hsl(223 100% 50%)", "hsl(160 84% 39%)",
    "hsl(262 83% 58%)", "hsl(0 0% 60%)", "hsl(180 100% 35%)", "hsl(330 80% 50%)",
    "hsl(30 90% 50%)", "hsl(120 70% 35%)",
  ];

  return (
    <div data-testid="stats-page">
      <PageHeader title="Estadísticas" subtitle={selected?.nombre}>
        <div className="flex items-center gap-2">
          <p className="overline">Año</p>
          <Select value={anio} onValueChange={setAnio}>
            <SelectTrigger className="w-28" data-testid="stats-year-select"><SelectValue /></SelectTrigger>
            <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </PageHeader>

      {loading || !data ? (
        <div className="data-card p-10 text-center text-muted-foreground">Cargando…</div>
      ) : (
        <div className="space-y-6">
          {/* KPIs */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <MiniStat label="Ingresos" value={eur(totalIngresos)} accent="success" />
            <MiniStat label="Gastos (operativos)" value={eur(totalSuministros)} accent="danger" sub={`Inversión: ${eur(totalGastos - totalSuministros)}`} />
            <MiniStat label="Balance neto" value={eur(totalBalance)} accent={totalBalance >= 0 ? "primary" : "danger"} />
            <MiniStat label="Ocupación media" value={`${ocupacion.length ? Math.round(ocupacion.reduce((s, o) => s + o.activos, 0) / ocupacion.length) : 0}`} accent="primary" sub="contratos/mes" />
            <MiniStat label="Puntualidad" value={totalPuntual ? `${Math.round(puntualidad.a_tiempo / totalPuntual * 100)}%` : "—"} accent={puntualidad.a_tiempo / totalPuntual >= 0.8 ? "success" : "warning"} sub={`${puntualidad.a_tiempo} de ${totalPuntual} pagos ≤5 días`} />
            <MiniStat label="Top inquilinos" value={topInquilinos.length} accent="primary" sub={topInquilinos[0]?.nombre || "—"} />
          </div>

          {/* Puntualidad explanation */}
          {totalPuntual > 0 && (
            <div className="flex items-start gap-2 p-3 rounded-md border border-border/60 bg-card text-xs text-muted-foreground">
              <Info className="h-4 w-4 shrink-0 mt-0.5" />
              <span>La puntualidad compara la <strong>fecha de cobro</strong> con la <strong>fecha de vencimiento</strong> del pago. Se considera "a tiempo" si se cobró en los 5 días siguientes al vencimiento.</span>
            </div>
          )}

          {/* Multi-year bar chart (operativo) */}
          <div className="data-card p-5">
            <h3 className="font-display font-medium mb-4">Evolución anual (operativo)</h3>
            <p className="text-xs text-muted-foreground -mt-3 mb-4">Ingresos vs gastos de suministros (sin inversiones)</p>
            {serieAnual.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">Sin datos multianuales</p>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={serieAnual} barGap={6}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={chartGrid} />
                  <XAxis dataKey="anio" tick={chartTick} axisLine={false} tickLine={false} />
                  <YAxis tick={{ ...chartTick, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} width={60} />
                  <Tooltip formatter={(v) => eur(v)} contentStyle={chartTooltip} />
                  <Legend wrapperStyle={{ fontSize: 13 }} />
                  <Bar dataKey="ingresos" name="Ingresos" fill="hsl(160 84% 39%)" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="suministros" name="Suministros" fill="hsl(4 100% 60%)" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Annual totals summary */}
          <div className="data-card p-5">
            <h3 className="font-display font-medium mb-3">Resumen anual completo · {anio}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="border border-border rounded-md p-3">
                <p className="text-xs text-muted-foreground">Total ingresos</p>
                <p className="font-mono text-xl font-semibold text-success mt-0.5" title={eur(totalIngresos)}>{eur(totalIngresos)}</p>
              </div>
              <div className="border border-border rounded-md p-3">
                <p className="text-xs text-muted-foreground">Total gastos</p>
                <p className="font-mono text-xl font-semibold text-destructive mt-0.5" title={eur(totalGastos)}>{eur(totalGastos)}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Suministros: {eur(totalSuministros)} · Inversión: {eur(totalGastos - totalSuministros)}
                </p>
              </div>
              <div className="border border-border rounded-md p-3">
                <p className="text-xs text-muted-foreground">Balance neto</p>
                <p className={`font-mono text-xl font-semibold mt-0.5 ${totalBalance >= 0 ? "text-primary" : "text-destructive"}`} title={eur(totalBalance)}>
                  {totalBalance >= 0 ? "" : "−"}{eur(Math.abs(totalBalance))}
                </p>
              </div>
            </div>
          </div>

          {/* Monthly + Occupancy row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Monthly breakdown line chart */}
            <div className="data-card p-5">
              <h3 className="font-display font-medium mb-4">Evolución mensual · {anio}</h3>
              {mensual.every((m) => !m.ingresos && !m.gastos) ? (
                <p className="text-sm text-muted-foreground py-8 text-center">Sin datos este año</p>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <ComposedChart data={mensual}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={chartGrid} />
                    <XAxis dataKey="mes" tick={chartTick} axisLine={false} tickLine={false} />
                    <YAxis yAxisId="left" tick={{ ...chartTick, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} width={64} />
                    <YAxis yAxisId="right" orientation="right" tick={chartTick} axisLine={false} tickLine={false} width={28} allowDecimals={false} />
                    <Tooltip formatter={(v, n) => n === "Inquilinos" ? v : eur(v)} contentStyle={chartTooltip} itemStyle={{ color: "hsl(var(--popover-foreground))" }} labelStyle={{ color: "hsl(var(--popover-foreground))" }} />
                    <Legend wrapperStyle={{ fontSize: 13 }} />
                    <Bar yAxisId="left" dataKey="ingresos" name="Ingresos" fill="hsl(160 84% 39%)" radius={[3, 3, 0, 0]} opacity={0.85} />
                    <Bar yAxisId="left" dataKey="gastos" name="Gastos" fill="hsl(4 100% 60%)" radius={[3, 3, 0, 0]} opacity={0.85} />
                    <Line yAxisId="right" type="monotone" dataKey="activos" name="Inquilinos" stroke="hsl(217 91% 60%)" strokeWidth={2} dot={false} strokeDasharray="4 3" />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Monthly occupancy bar chart */}
            <div className="data-card p-5">
              <h3 className="font-display font-medium mb-4">Ocupación mensual · {anio}</h3>
              {ocupacion.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">Sin datos</p>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={ocupacion.map((o) => ({ ...o, mes: MESES[(o.mes || 1) - 1] }))} barGap={4}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={chartGrid} />
                    <XAxis dataKey="mes" tick={chartTick} axisLine={false} tickLine={false} />
                    <YAxis tick={chartTick} axisLine={false} tickLine={false} width={40} allowDecimals={false} />
                    <Tooltip contentStyle={chartTooltip} />
                    <Bar dataKey="activos" name="Contratos activos" fill="hsl(217 91% 60%)" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Bottom row: top tenants + expenses breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top tenants */}
            <div className="data-card overflow-hidden">
              <div className="p-4 border-b border-border">
                <h3 className="font-display font-medium">Top inquilinos · {anio}</h3>
              </div>
              {topInquilinos.length === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground">Sin pagos este año</div>
              ) : (
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>#</TableHead><TableHead>Inquilino</TableHead><TableHead className="text-right">Total pagado</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {topInquilinos.map((t, i) => (
                      <TableRow key={t.tenant_id}>
                        <TableCell className="text-muted-foreground w-8">{i + 1}</TableCell>
                        <TableCell className="font-medium">{t.nombre}</TableCell>
                        <TableCell className="text-right font-mono">{eur(t.total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>

            {/* Expenses by concept */}
            <div className="data-card p-5">
              <h3 className="font-display font-medium mb-4">Gastos por concepto · {anio}</h3>
              {gastosPorConcepto.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">Sin gastos este año</p>
              ) : (
                <div className="flex flex-col sm:flex-row items-center gap-6">
                  <ResponsiveContainer width="100%" height={200} className="max-w-[220px]">
                    <PieChart>
                      <Pie data={gastosPorConcepto} dataKey="total" nameKey="concepto" cx="50%" cy="50%" outerRadius={80} innerRadius={45}>
                        {gastosPorConcepto.map((_, i) => (
                          <Cell key={i} fill={GASTO_COLORS[i % GASTO_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => eur(v)} contentStyle={chartTooltip} itemStyle={{ color: "hsl(var(--popover-foreground))" }} labelStyle={{ color: "hsl(var(--popover-foreground))" }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-1.5 text-sm w-full">
                    {gastosPorConcepto.map((g, i) => (
                      <div key={g.concepto} className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <span className="h-3 w-3 rounded-sm inline-block" style={{ backgroundColor: GASTO_COLORS[i % GASTO_COLORS.length] }} />
                          <span className="text-muted-foreground truncate">{g.concepto}</span>
                        </div>
                        <span className="font-mono font-medium">{eur(g.total)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Expense evolution per concept */}
          {(data?.gastos_evolucion || []).length > 0 && (
            <div className="data-card p-5">
              <h3 className="font-display font-medium mb-1">Evolución de gastos por tipo · {anio}</h3>
              <p className="text-xs text-muted-foreground mb-4">Importe mensual de cada concepto de gasto</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {data.gastos_evolucion.map((g) => {
                  const maxVal = Math.max(...g.mensual, 1);
                  const gastoData = g.mensual.map((val, i) => ({ mes: MESES[i], valor: Math.round(val) }));
                  const total = g.mensual.reduce((s, v) => s + v, 0);
                  return (
                    <div key={g.concepto} className="border border-border rounded-lg p-3">
                      <p className="font-medium text-sm mb-1">{g.concepto}</p>
                      <p className="font-mono text-lg font-semibold">{eur(Math.round(total))}</p>
                      <div className="mt-2 h-10">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={gastoData} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                            <XAxis dataKey="mes" hide />
                            <Tooltip formatter={(v) => eur(v)} contentStyle={chartTooltip} itemStyle={{ color: "hsl(var(--popover-foreground))" }} labelStyle={{ color: "hsl(var(--popover-foreground))", fontSize: 11 }} />
                            <Bar dataKey="valor" fill="hsl(var(--primary))" radius={[2, 2, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
